import React, { useState, useEffect } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

export type WorkLogPopupMode = 'create' | 'edit' | 'view'

export interface WorkLogFormData {
  logId: string
  logNo: string
  taskId: string
  employeeCode: string
  workDate: string
  hoursLogged: number
  description: string
  totalCall: number | null  // Allow null for totalCall
  taskName?: string // For display purposes
}

export interface WorkLog {
  logId: string
  logNo: string
  taskId: string
  employeeCode: string
  workDate: string
  hoursLogged: number
  description: string
  totalCall: number | null  // Allow null for totalCall
  createdAt?: string
  // Additional fields for display
  employeeName?: string
  taskName?: string
}

export interface Employee {
  ID: number
  EmployeeID: number
  EmployeeCode: string
  EmployeeName: string
  Email: string
  MobileNumber: string
  Department: string
  Designation: string
  EmploymentStatus: string
  IsActive: boolean
}

export interface Task {
  taskId: string
  taskName: string
  task: string
  status: string
}

interface WorkLogPopupProps {
  isOpen: boolean
  onClose: () => void
  onSave: (workLog: WorkLogFormData) => void
  onDelete?: () => void
  workLog?: WorkLog | null
  task?: Task | null  // Single task for context (when logging from a specific task)
  mode: WorkLogPopupMode
  employees?: Employee[]
  tasks?: Task[]       // List of tasks for selection
  loggedInEmployeeCode: string | null
  isSubmitting?: boolean
  submitError?: string | null
  successMessage?: string | null
}

// ── Constants ─────────────────────────────────────────────────────────────────

const defaultWorkLog: WorkLogFormData = {
  logId: '',
  logNo: '',
  taskId: '',
  employeeCode: '',
  workDate: new Date().toISOString().split('T')[0],
  hoursLogged: 0,
  description: '',
  totalCall: null  // Default to null instead of 0
}

// ── Styles ────────────────────────────────────────────────────────────────────

const workLogPopupStyles = `
  @keyframes modalFadeIn {
    from { opacity: 0; backdrop-filter: blur(0); }
    to { opacity: 1; backdrop-filter: blur(8px); }
  }
  @keyframes modalSlideIn {
    from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  @keyframes modalShimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .wl-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(4, 17, 26, 0.85);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: modalFadeIn 0.25s ease both;
  }

  .wl-modal-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 700px;
    max-height: 90vh;
    overflow-y: auto;
    background: linear-gradient(160deg, #0e1c2a 0%, #0a1a28 100%);
    border: 1px solid rgba(0, 210, 200, 0.3);
    border-radius: 24px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(0, 210, 200, 0.2) inset;
    animation: modalSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .wl-modal-shimmer {
    height: 4px;
    background: linear-gradient(90deg, transparent, #00d2c8, #48cae4, #00d2c8, transparent);
    background-size: 200% 100%;
    animation: modalShimmer 3s linear infinite;
    border-radius: 24px 24px 0 0;
  }

  .wl-modal-header {
    padding: 20px 28px;
    background: linear-gradient(90deg, rgba(0, 160, 155, 0.15) 0%, rgba(0, 120, 175, 0.1) 100%);
    border-bottom: 1px solid rgba(0, 210, 200, 0.2);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .wl-modal-title {
    font-size: 20px;
    font-weight: 700;
    color: #e8f4f6;
    letter-spacing: 0.02em;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .wl-modal-title-icon {
    font-size: 28px;
    filter: drop-shadow(0 0 8px rgba(0, 210, 200, 0.4));
  }

  .wl-modal-close {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: rgba(0, 210, 200, 0.1);
    border: 1px solid rgba(0, 210, 200, 0.25);
    color: #7ab0b8;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
  }

  .wl-modal-close:hover {
    background: rgba(0, 210, 200, 0.2);
    color: #00d2c8;
    transform: rotate(90deg);
  }

  .wl-modal-body {
    padding: 28px;
    background: #c5d7d5;
  }

  .wl-modal-section {
    background: linear-gradient(160deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.3));
    border-radius: 20px;
    padding: 24px;
    margin-bottom: 24px;
    border: 1px solid rgba(0, 100, 95, 0.25);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }

  .wl-modal-section-title {
    font-size: 14px;
    font-weight: 700;
    color: #1a5558;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid rgba(0, 100, 95, 0.25);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .wl-modal-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .wl-modal-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .wl-modal-field-full {
    grid-column: 1 / -1;
  }

  .wl-modal-label {
    font-size: 12px;
    font-weight: 700;
    color: #1a5558;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .wl-modal-label-required {
    color: #c03030;
    font-size: 14px;
  }

  .wl-modal-input {
    width: 100%;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.8);
    border: 2px solid rgba(0, 100, 95, 0.25);
    border-radius: 12px;
    font-size: 14px;
    color: #0b2d30;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    transition: all 0.2s;
    outline: none;
  }

  .wl-modal-input:focus {
    border-color: #007a74;
    box-shadow: 0 0 0 4px rgba(0, 120, 115, 0.2);
    background: rgba(255, 255, 255, 0.95);
  }

  .wl-modal-input:hover:not(:focus) {
    border-color: rgba(0, 100, 95, 0.4);
    background: rgba(255, 255, 255, 0.9);
  }

  .wl-modal-input:disabled {
    background: rgba(200, 200, 200, 0.3);
    border-color: rgba(0, 100, 95, 0.15);
    color: #2a7070;
    cursor: not-allowed;
  }

  textarea.wl-modal-input {
    min-height: 100px;
    resize: vertical;
  }

  .wl-modal-select {
    width: 100%;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.8);
    border: 2px solid rgba(0, 100, 95, 0.25);
    border-radius: 12px;
    font-size: 14px;
    color: #0b2d30;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='%23007a74' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    background-size: 18px;
  }

  .wl-modal-select:disabled {
    background: rgba(200, 200, 200, 0.3);
    border-color: rgba(0, 100, 95, 0.15);
    color: #2a7070;
    cursor: not-allowed;
  }

  .wl-modal-select option {
    background: #ddecea;
    color: #0b2d30;
    padding: 8px;
  }

  .wl-modal-footer {
    padding: 20px 28px;
    background: rgba(0, 0, 0, 0.3);
    border-top: 1px solid rgba(0, 210, 200, 0.2);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
  }

  .wl-modal-btn {
    padding: 12px 28px;
    border-radius: 40px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: none;
    font-family: 'DM Sans', sans-serif;
  }

  .wl-modal-btn-primary {
    background: linear-gradient(135deg, #007a74, #005f6b);
    color: white;
    box-shadow: 0 4px 15px rgba(0, 122, 116, 0.4);
  }

  .wl-modal-btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #008a84, #006f7b);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 122, 116, 0.5);
  }

  .wl-modal-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .wl-modal-btn-secondary {
    background: rgba(0, 210, 200, 0.1);
    border: 1px solid rgba(0, 210, 200, 0.3);
    color: #7ab0b8;
  }

  .wl-modal-btn-secondary:hover:not(:disabled) {
    background: rgba(0, 210, 200, 0.18);
    color: #00d2c8;
  }

  .wl-modal-btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .wl-modal-btn-danger {
    background: rgba(192, 48, 48, 0.15);
    border: 1px solid rgba(192, 48, 48, 0.3);
    color: #c03030;
  }

  .wl-modal-btn-danger:hover:not(:disabled) {
    background: rgba(192, 48, 48, 0.25);
  }

  .wl-modal-btn-danger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .wl-modal-error {
    background: rgba(192, 48, 48, 0.15);
    border: 1px solid rgba(192, 48, 48, 0.4);
    border-radius: 12px;
    padding: 12px 16px;
    color: #c03030;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .wl-modal-success {
    background: rgba(0, 160, 140, 0.15);
    border: 1px solid rgba(0, 160, 140, 0.4);
    border-radius: 12px;
    padding: 12px 16px;
    color: #004a46;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .wl-info-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: rgba(0, 100, 95, 0.1);
    border: 1px solid rgba(0, 100, 95, 0.2);
    border-radius: 8px;
    font-size: 12px;
    color: #004a46;
  }

  .wl-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
  }

  .wl-hours-display {
    font-size: 24px;
    font-weight: 700;
    color: #007a74;
    text-align: center;
    padding: 10px;
    background: rgba(0, 122, 116, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(0, 122, 116, 0.2);
  }
`

// ── Component ─────────────────────────────────────────────────────────────────

const WorkLogPopup: React.FC<WorkLogPopupProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  workLog, 
  task,  // Single task for context
  mode,
  loggedInEmployeeCode,
  employees = [],
  tasks = [],
  isSubmitting = false,
  submitError = null,
  successMessage = null
}) => {
  const [formData, setFormData] = useState<WorkLogFormData>(defaultWorkLog)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)

  // Get employee code from sessionStorage - ALWAYS use this
  const sessionEmployeeCode = sessionStorage.getItem("employeeCode") || ''

  // Generate a unique log ID
  const generateLogId = (): string => {
    const timestamp = Date.now().toString().slice(-8)
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
    return `LOG-${timestamp}${random}`
  }

  // Generate a unique log number
  const generateLogNo = (): string => {
    const date = new Date()
    const year = date.getFullYear().toString().slice(-2)
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
    return `WL${year}${month}${day}-${random}`
  }

  // Reset form when modal opens/closes or mode/workLog changes
  useEffect(() => {
    if (isOpen) {
      if (workLog && (mode === 'edit' || mode === 'view')) {
        setFormData({
          logId: workLog.logId,
          logNo: workLog.logNo,
          taskId: workLog.taskId,
          employeeCode: sessionEmployeeCode, // Force session employee code
          workDate: workLog.workDate,
          hoursLogged: workLog.hoursLogged,
          description: workLog.description || '',
          totalCall: workLog.totalCall // Keep original totalCall (could be null)
        })

        // Find and set selected task from tasks list
        const foundTask = tasks.find(t => t.taskId === workLog.taskId)
        setSelectedTask(foundTask || null)
        
      } else if (mode === 'create') {
        console.log('Setting default employee code from session:', sessionEmployeeCode) // For debugging
        
        // Generate new IDs for create mode
        setFormData({
          logId: generateLogId(),
          logNo: generateLogNo(),
          taskId: task?.taskId || '',
          employeeCode: sessionEmployeeCode, // Force session employee code
          workDate: new Date().toISOString().split('T')[0],
          hoursLogged: 0,
          description: '',
          totalCall: null  // Set to null for create mode
        })
        
        // Set selected task from the task prop if provided
        setSelectedTask(task || null)
      }
      
      // Clear errors when opening
      setErrors({})
    }
  }, [isOpen, workLog, task, mode, tasks, sessionEmployeeCode])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    
    // Don't allow changing employee code
    if (name === 'employeeCode') {
      return
    }
    
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // If task changes, update selected task
    if (name === 'taskId') {
      const foundTask = tasks.find(t => t.taskId === value)
      setSelectedTask(foundTask || null)
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    
    // Don't allow changing employee code
    if (name === 'employeeCode') {
      return
    }
    
    if (name === 'totalCall') {
      // For totalCall, allow empty string to represent null
      const numValue = value === '' ? null : parseInt(value, 10)
      setFormData(prev => ({ ...prev, [name]: numValue }))
    } else {
      const numValue = value === '' ? 0 : parseFloat(value)
      setFormData(prev => ({ ...prev, [name]: numValue }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.logId.trim()) newErrors.logId = 'Log ID is required'
    if (!formData.logNo.trim()) newErrors.logNo = 'Log No is required'
    if (!formData.taskId) newErrors.taskId = 'Task is required'
    if (!formData.employeeCode) newErrors.employeeCode = 'Employee is required'
    if (!formData.workDate) newErrors.workDate = 'Work date is required'
    
    // Validate hours
    if (formData.hoursLogged <= 0) {
      newErrors.hoursLogged = 'Hours must be greater than 0'
    } else if (formData.hoursLogged > 24) {
      newErrors.hoursLogged = 'Hours cannot exceed 24 in a day'
    }
    
    // Validate total call - allow null as valid
    if (formData.totalCall !== null && formData.totalCall < 0) {
      newErrors.totalCall = 'Total call cannot be negative'
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (mode === 'view') {
      onClose()
      return
    }
    
    if (validateForm() && !isSubmitting) {
      onSave(formData)
    }
  }

  const getEmployeeDisplay = (employeeCode: string) => {
    if (!employeeCode) return '—'
    const emp = employees.find(e => e.EmployeeCode === employeeCode)
    return emp ? `${emp.EmployeeName} (${emp.EmployeeCode})` : employeeCode
  }

  const getEmployeeOptions = () => {
    if (employees.length === 0) {
      return <option value="">No employees available</option>
    }
    
    return employees
      .filter(emp => emp.IsActive !== false)
      .map(emp => (
        <option key={emp.EmployeeCode} value={emp.EmployeeCode}>
          {emp.EmployeeName} ({emp.EmployeeCode}) - {emp.Designation}
        </option>
      ))
  }

  const getTaskOptions = () => {
    if (tasks.length === 0) {
      return <option value="">No tasks available</option>
    }
    
    return tasks.map(t => (
      <option key={t.taskId} value={t.taskId}>
        {t.taskId} - {t.taskName || t.task}
      </option>
    ))
  }

  if (!isOpen) return null

  const isViewMode = mode === 'view'
  const isTaskPreSelected = !!task && mode === 'create'
  const title = mode === 'create' ? 'Log Work Hours' : mode === 'edit' ? 'Edit Work Log' : 'Work Log Details'
  const titleIcon = mode === 'create' ? '⏱️' : mode === 'edit' ? '📝' : '🔍'

  // Debug: Log current form data
  console.log('Current form data:', formData)

  return (
    <>
      <style>{workLogPopupStyles}</style>
      <div className="wl-modal-overlay" onClick={onClose}>
        <div className="wl-modal-container" onClick={e => e.stopPropagation()}>
          <div className="wl-modal-shimmer" />
          
          <div className="wl-modal-header">
            <div className="wl-modal-title">
              <span className="wl-modal-title-icon">{titleIcon}</span>
              {title}
            </div>
            <div className="wl-modal-close" onClick={onClose}>✕</div>
          </div>

          <div className="wl-modal-body">
            {/* Validation Errors */}
            {Object.keys(errors).length > 0 && (
              <div className="wl-modal-error">
                <span>⚠️</span>
                Please fix the errors before saving
              </div>
            )}

            {/* API Error */}
            {submitError && (
              <div className="wl-modal-error">
                <span>❌</span>
                {submitError}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="wl-modal-success">
                <span>✅</span>
                {successMessage}
              </div>
            )}

            {/* Task Info Section - Show selected task context */}
            {(selectedTask || task) && (
              <div className="wl-info-badge" style={{ marginBottom: '20px' }}>
                <span>📋</span>
                <span>
                  <strong>Task:</strong> {selectedTask?.taskId || task?.taskId} - {selectedTask?.taskName || task?.taskName || task?.task}
                </span>
                <span style={{ marginLeft: 'auto' }}>
                  <strong>Status:</strong> {selectedTask?.status || task?.status || 'N/A'}
                </span>
              </div>
            )}

            {/* Work Log Section */}
            <div className="wl-modal-section">
              <div className="wl-modal-section-title">
                <span>⏱️</span> Work Log Details
              </div>
              
              <div className="wl-modal-grid">
                <div className="wl-modal-field">
                  <label className="wl-modal-label">
                    Log ID <span className="wl-modal-label-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="logId"
                    value={formData.logId}
                    onChange={handleChange}
                    className="wl-modal-input"
                    placeholder="Auto-generated"
                    disabled={true}
                  />
                </div>

                <div className="wl-modal-field">
                  <label className="wl-modal-label">
                    Log No <span className="wl-modal-label-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="logNo"
                    value={formData.logNo}
                    onChange={handleChange}
                    className="wl-modal-input"
                    placeholder="Auto-generated"
                    disabled={true}
                  />
                </div>

                <div className="wl-modal-field">
                  <label className="wl-modal-label">
                    Task <span className="wl-modal-label-required">*</span>
                  </label>
                  {isViewMode || isTaskPreSelected ? (
                    <div className="wl-modal-input" style={{background: 'rgba(200,200,200,0.3)'}}>
                      {formData.taskId} - {selectedTask?.taskName || task?.taskName || 'Unknown Task'}
                    </div>
                  ) : (
                    <select 
                      name="taskId" 
                      value={formData.taskId}
                      onChange={handleChange}
                      className="wl-modal-select"
                      disabled={isSubmitting}
                    >
                      <option value="">Select Task</option>
                      {getTaskOptions()}
                    </select>
                  )}
                  {errors.taskId && <span style={{color:'#c03030', fontSize:'12px'}}>{errors.taskId}</span>}
                </div>

                <div className="wl-modal-field">
                  <label className="wl-modal-label">
                    Employee <span className="wl-modal-label-required">*</span>
                  </label>
                  {isViewMode ? (
                    <div className="wl-modal-input" style={{background: 'rgba(200,200,200,0.3)'}}>
                      {getEmployeeDisplay(formData.employeeCode)}
                    </div>
                  ) : (
                    <select 
                      name="employeeCode" 
                      value={formData.employeeCode}
                      onChange={handleChange}
                      className="wl-modal-select"
                      disabled={true} // Always disabled
                    >
                      <option value="">Select Employee</option>
                      {getEmployeeOptions()}
                    </select>
                  )}
                  {errors.employeeCode && <span style={{color:'#c03030', fontSize:'12px'}}>{errors.employeeCode}</span>}
                </div>

                <div className="wl-modal-field">
                  <label className="wl-modal-label">
                    Work Date <span className="wl-modal-label-required">*</span>
                  </label>
                  <input
                    type="date"
                    name="workDate"
                    value={formData.workDate}
                    onChange={handleChange}
                    className="wl-modal-input"
                    disabled={isViewMode || isSubmitting}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  {errors.workDate && <span style={{color:'#c03030', fontSize:'12px'}}>{errors.workDate}</span>}
                </div>

                <div className="wl-modal-field">
                  <label className="wl-modal-label">
                    Total Call
                  </label>
                  <input
                    type="number"
                    name="totalCall"
                    value={formData.totalCall === null ? '' : formData.totalCall}
                    onChange={handleNumberChange}
                    className="wl-modal-input"
                    step="1"
                    min="0"
                    placeholder="Enter total calls"
                    disabled={isViewMode || isSubmitting}
                  />
                  {errors.totalCall && <span style={{color:'#c03030', fontSize:'12px'}}>{errors.totalCall}</span>}
                </div>
              </div>

              <div className="wl-modal-field" style={{ marginTop: '20px' }}>
                <label className="wl-modal-label">
                  Hours Logged <span className="wl-modal-label-required">*</span>
                </label>
                {isViewMode ? (
                  <div className="wl-hours-display">
                    {formData.hoursLogged} hours
                  </div>
                ) : (
                  <>
                    <input
                      type="number"
                      name="hoursLogged"
                      value={formData.hoursLogged}
                      onChange={handleNumberChange}
                      className="wl-modal-input"
                      min="0.5"
                      max="24"
                      step="0.5"
                      disabled={isSubmitting}
                    />
                    <small style={{ color: '#1a5558', marginTop: '4px' }}>
                      Enter hours in increments of 0.5 (max 24 hours)
                    </small>
                  </>
                )}
                {errors.hoursLogged && <span style={{color:'#c03030', fontSize:'12px'}}>{errors.hoursLogged}</span>}
              </div>

              <div className="wl-modal-field wl-modal-field-full" style={{ marginTop: '20px' }}>
                <label className="wl-modal-label">
                  Description <span className="wl-modal-label-required">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="wl-modal-input"
                  placeholder="Describe the work done..."
                  disabled={isViewMode || isSubmitting}
                  rows={4}
                />
                {errors.description && <span style={{color:'#c03030', fontSize:'12px'}}>{errors.description}</span>}
                {!isViewMode && (
                  <small style={{ color: '#1a5558', marginTop: '4px' }}>
                    {formData.description.length} / 500 characters
                  </small>
                )}
              </div>
            </div>
          </div>

          <div className="wl-modal-footer">
            {mode !== 'create' && onDelete && mode !== 'view' && (
              <button 
                className="wl-modal-btn wl-modal-btn-danger"
                onClick={onDelete}
                disabled={isSubmitting}
              >
                🗑️ Delete Log
              </button>
            )}
            
            <button 
              className="wl-modal-btn wl-modal-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            
            <button 
              className="wl-modal-btn wl-modal-btn-primary"
              onClick={handleSubmit}
              disabled={isViewMode || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="wl-spinner" />
                  {mode === 'create' ? 'Logging...' : 'Saving...'}
                </>
              ) : (
                <>
                  {mode === 'create' && '⏱️ Log Hours'}
                  {mode === 'edit' && '💾 Save Changes'}
                  {mode === 'view' && '✓ Close'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default WorkLogPopup